import React, {useEffect, useState} from "react";
import {useAsyncDebounce, useGlobalFilter, usePagination, useRowState, useSortBy, useTable, useFilters, useExpanded} from "react-table";
import {formattedColumns, StyledTable, TableBody, TableHead, DefaultColumnFilter} from "./TableCommon";
import {Loader} from "../loader/Loader";

function Table({setPaginationProps,
                 setTableSearchProps,
                 setTablePageLengthProps,
                 setSortByProps,
                 setTableFilterProps,
                 async,
                 totalSize,
                 pageCount,
                 loaderProps,
                 noPadding,
                 autoWidth = true,
                 ...props}) {

  const data = React.useMemo(() => props.data || [], [props.data]);
  const columns = React.useMemo(() => formattedColumns(props.columns), [props.columns]);
  const [highlightedRowIndex, setHighlightedRowIndex] = useState(null)
  const [paginate, setPaginate] = useState(props.paginate)
  // Check if this works or if data & columns get recreated without the props being memoized in parent component

  const options = {
    sortBy: {
      [columns[0].accessor]: 'asc'
    },
    sortable: true,
    paginate: false,
    header: true,
    ...props
  }
  const initialSortBy = Object.keys(options.sortBy).map( (key) => ({id: key, desc: options.sortBy[key].toLowerCase() === 'desc'}))
  const initialFilters = React.useMemo(() => {
    return props.initialFilters || []
  }, []);

  useEffect(() => {
    if (data.length > 10) {
      setPaginate(props.paginate)
    } else {
      setPaginate(false)
    }
  }, [props.paginate, data.length])

  useEffect(() => {
    if (highlightedRowIndex != null) {
      setRowState([highlightedRowIndex], currentState => ({...currentState, highlighted: false}))
    }
    if (props.highlighted != null) {
      setRowState([props.highlighted], currentState => ({...currentState, highlighted: true}))
    }
    setHighlightedRowIndex(props.highlighted)
  }, [props.highlighted])

  const hiddenColumnsProps = props.hiddenColumns ? {
    hiddenColumns: props.hiddenColumns
  } : {}

  const asyncProps = async ? {
    manualPagination: true,
    manualSortBy: true,
    manualGlobalFilter: true,
    manualFilters: true,
    pageCount
  } : {}

  const defaultColumn = React.useMemo(
    () => ({
      filter: (rows, columnIds, filterValue) => {
        const columnId = columnIds[0]
        if (filterValue) {
          return rows.filter(row => {
            // const index = filterValue.findIndex(value => row.values[columnId].toLowerCase() === value.toLowerCase());
            const index = filterValue.findIndex(value => row.original[columnId].toLowerCase() === value.toLowerCase());
            return index > -1
          })
        } else {
          return rows
        }
      },
      Filter: DefaultColumnFilter,
      disableFilters: true
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setRowState,
    setFilter,
    setAllFilters,
    state: { pageIndex, pageSize, sortBy, globalFilter, filters }
  } = useTable({
    columns,
    data,
    initialState: {
      sortBy: initialSortBy,
      ...hiddenColumnsProps,
      filters: initialFilters,
      pageIndex: 0
    },
    disableSortBy: !options.sortable,
    ...asyncProps,
    defaultColumn,
  }, useGlobalFilter, useFilters, useSortBy, useExpanded, usePagination, useRowState)

  useEffect(() => {
    setPaginationProps({
      pageIndex: pageIndex + 1,
      pageSize: pageSize,
      pageCount: pageOptions.length,
      totalSize: totalSize || data.length,
      canNextPage: canNextPage,
      canPreviousPage: canPreviousPage,
      nextPage: nextPage,
      previousPage: previousPage,
      gotoPage: gotoPage,
      ready: true
    })
  }, [pageIndex, pageSize, pageOptions.length, data.length, totalSize, canNextPage, canPreviousPage, nextPage, previousPage, gotoPage])

  const onFilterChange = useAsyncDebounce(value => {
      setGlobalFilter(value || undefined)
  }, 200)

  const [displayedFilterValue, setDisplayedFilterValue] = useState()
  useEffect(() => {
    onFilterChange(displayedFilterValue)
  }, [displayedFilterValue])

  useEffect(() => {
    setDisplayedFilterValue(globalFilter)
  }, [globalFilter])

  useEffect(() => {
    setTableSearchProps({
      onChange: setDisplayedFilterValue,
      value: displayedFilterValue,
      globalFilter: globalFilter,
      ready: true
    })
  }, [displayedFilterValue, globalFilter])

  useEffect(() => {
    setTablePageLengthProps({
      setPageSize,
      pageSize: pageSize,
      ready: true
    })
  }, [pageSize, setPageSize])

  useEffect(() => {
    setSortByProps({
      sortBy
    })
  }, [JSON.stringify(sortBy)])

  useEffect(() => {
    setTableFilterProps({
      filters,
      setFilter,
      setAllFilters
    })
  }, [filters])

  return (
    <>
      <StyledTable noPadding {...getTableProps()} clickable={props.onRowClick} hoverable={props.onRowHover} small={props.small}>
        {options.header && <TableHead headerGroups={headerGroups} autoWidth={autoWidth}/>}
        <TableBodyWrapper async={async} loaderProps={loaderProps}>
          <TableBody
            tableBodyProps={getTableBodyProps()}
            paginate={paginate}
            prepareRow={prepareRow}
            page={page}
            rows={rows}
            onRowHover={props.onRowHover}
            onRowClick={props.onRowClick}
            autoWidth={autoWidth}
            getRowProps={props.getRowProps}/>
        </TableBodyWrapper>
      </StyledTable>
    </>
  )
}

export default React.memo(Table)

function TableBodyWrapper({async, loaderProps, children}) {
  if (async) {
    return <Loader {...loaderProps} as={Tbody}>
      {children}
    </Loader>
  } else {
    return (<>
      {children}
    </>)
  }
}

function Tbody({children, ...props}) {
  return <tbody {...props}>
    <tr>
      <td colSpan="100%">
        {children}
      </td>
    </tr>
  </tbody>
}
